/**
 * 极简紫韵模版 - 列表页
 * 
 * 设计理念：左侧树形分类导航栏 + 右侧紧凑横条列表
 * 与所有现有模板（标签栏+瀑布流/Bento网格）完全不同：
 * - 左栏：可折叠的树形分类目录（sticky固定）
 * - 右栏：紧凑横条式信息列表（左缩略图+右信息）
 * - 排序：顶部内嵌选择器
 * - 分页：无限滚动加载更多
 */

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import PurpleNavbar from "@/templates/purple/components/Navbar";
import PurpleFooter from "@/templates/purple/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布" },
  { key: "popular", label: "最多浏览" },
  { key: "alpha", label: "名称排序" },
] as const;

export default function PurpleCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // 无限滚动
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载数据
  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    setCurrentPage(1);
    const params: Record<string, any> = { page: 1, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, sortBy]);

  // 加载更多
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || listings.length >= total) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const params: Record<string, any> = { page: nextPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    try {
      const data = await getListings(params);
      setListings(prev => [...prev, ...(data.list || [])]);
      setCurrentPage(nextPage);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, listings.length, total, currentPage, activeCategoryId]);

  // 无限滚动检测
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && listings.length < total) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, loadingMore, listings.length, total, handleLoadMore]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;

  return (
    <div className="min-h-screen bg-gray-50/50">
      <PurpleNavbar />

      {/* 面包屑 */}
      <div className="pt-20 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-1.5 text-sm py-3">
            <Link to="/" className="text-gray-400 hover:text-purple-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <span className="text-purple-600 font-semibold">{currentCategoryName}</span>
          </nav>
        </div>
      </div>

      {/* 主内容：左栏分类 + 右栏列表 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* ======== 左侧：树形分类导航 ======== */}
          <aside className="lg:w-56 shrink-0">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-50">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <i className="ri-menu-2-line text-purple-500" />
                  全部分类
                </h3>
              </div>
              <nav className="p-2 max-h-[60vh] overflow-y-auto">
                <button
                  onClick={() => setActiveCategoryId("")}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                    !activeCategoryId
                      ? "bg-purple-50 text-purple-600 shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 hover:text-purple-600"
                  }`}
                >
                  <i className="ri-apps-line text-purple-400" />
                  全部
                  <span className="ml-auto text-xs text-gray-400">{total}</span>
                </button>
                {displayCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                      activeCategoryId === cat.id
                        ? "bg-purple-50 text-purple-600 shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-purple-600"
                    }`}
                  >
                    <i className={`${cat.icon || 'ri-folder-line'} text-purple-400`} />
                    {cat.name}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* ======== 右侧：列表区域 ======== */}
          <div className="flex-1 min-w-0">
            {/* 顶部操作栏 */}
            <div className="flex items-center justify-end mb-4 bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3">
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none pl-4 pr-9 py-2 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 cursor-pointer"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* 列表 */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-2xl border border-gray-100 p-4 flex gap-4">
                    <div className="w-24 h-24 bg-gray-200 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <div className="w-3/4 h-4 bg-gray-200 rounded" />
                      <div className="w-full h-3 bg-gray-100 rounded" />
                      <div className="w-1/2 h-3 bg-gray-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedListings.length > 0 ? (
              <div className="space-y-3">
                {sortedListings.map((listing) => (
                  <Link
                    key={listing.id}
                    to={`/listing/${listing.id}`}
                    className="group bg-white rounded-2xl border border-gray-100 hover:shadow-md hover:border-purple-200 transition-all flex gap-4 p-4 overflow-hidden"
                  >
                    {/* 缩略图 */}
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-purple-50 overflow-hidden shrink-0">
                      {listing.images?.[0] ? (
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-purple-300 text-xl" />
                        </div>
                      )}
                    </div>

                    {/* 信息 */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                      <div>
                        <div className="flex items-start gap-2">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-800 line-clamp-1 group-hover:text-purple-600 transition-colors flex-1">
                            {listing.title}
                          </h3>
                          {listing.is_pinned && (
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 bg-purple-500 text-white rounded-full font-bold">
                              顶
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">
                          {listing.content?.substring(0, 100) || "暂无描述"}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span className="text-[11px] px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                          {listing.category?.name || "未分类"}
                        </span>
                        <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(listing.created_at)}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.view_count || 0}</span>
                        {listing.location && (
                          <span className="hidden sm:flex items-center gap-1"><i className="ri-map-pin-line" />{listing.location}</span>
                        )}
                      </div>
                    </div>

                    {/* 箭头 */}
                    <div className="hidden sm:flex items-center shrink-0">
                      <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-purple-500 transition-colors text-xl" />
                    </div>
                  </Link>
                ))}

                {/* 加载更多的触发器 */}
                {listings.length < total && (
                  <div ref={loaderRef} className="py-6 text-center">
                    {loadingMore ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                        <div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        加载中...
                      </div>
                    ) : (
                      <button
                        onClick={handleLoadMore}
                        className="text-sm text-purple-500 hover:text-purple-600 font-medium"
                      >
                        加载更多 <i className="ri-arrow-down-line" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-purple-50">
                  <i className="ri-inbox-line text-2xl text-purple-400" />
                </div>
                <p className="text-gray-500 font-medium">该分类暂无信息</p>
                <Link to="/" className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm font-bold rounded-2xl hover:from-purple-700 hover:to-violet-700 transition-all shadow-md">
                  <i className="ri-arrow-left-line" />返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <PurpleFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
